import AlarmIcon from '@mui/icons-material/Alarm';
import CampaignIcon from '@mui/icons-material/Campaign';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import Launch from '@mui/icons-material/Launch';
import PersonIcon from '@mui/icons-material/Person';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { CheckIcon, ClockIcon } from 'assets/icons';
import useCurrentUser from 'hooks/useCurrentUser';
import type React from 'react';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useCancelScheduledMessageMutation } from 'services/api/scheduledMessages.api';
import type { Lead } from 'services/types/lead';
import type { Message } from 'services/types/message';
import type { ScheduledMessage } from 'services/types/scheduledMessage';
import {
  LIGHT_GREY,
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SHADOW,
  SHADOW_CARD,
  SHADOW_DARK_CARD,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  SURFACE_TINT,
  YELLOW,
} from 'styles/tokens';
import { BCI_REFI_ROBOT_BUSINESS_UNIT_ID } from 'utils/constants';
import { extractFilenameFromUrl, getFileExtensionFromUrl } from 'utils/fileUtils';
import textToReactNodes from 'utils/textToReactNodes';

const filterOutStatuses = ['neverSent', 'deleted', 'discarded', 'failed'];

const getExtension = (fileUrl: string): string => {
  // Use the utility function for getting file extension from URLs, including signed URLs
  return getFileExtensionFromUrl(fileUrl);
};

interface MessageForDisplay extends Message {
  referenceMessage?: Message | null;
  carouselCards?: Message[];
}

interface Props {
  conversation: Message[];
  scheduledMessages?: ScheduledMessage[];
  isFilteredConversation: boolean;
  fullHeight?: boolean;
  /** Smaller bubbles and text (e.g. funnel chat side panel). */
  compact?: boolean;
  isClientView?: boolean;
  /** Overrides the outgoing bubble background in client view (white-label widget). */
  outgoingBubbleColor?: string;
  /** Overrides the outgoing bubble text color in client view (white-label widget). */
  outgoingBubbleTextColor?: string;
  isAssistantWriting?: boolean;
  /** Hides per-message timestamps and delivery ticks (end-customer widget view). */
  hideMessageMeta?: boolean;
  newMessageChunk?: string;
  onQuickReplyClick?: (text: string) => void;
  /** Used (with businessUnitId) to render synthetic "Cliente enviado a CRM" bubbles for Salfa BUs. */
  leads?: Pick<Lead, 'id' | 'CRMUpdateAt' | 'crmUploadReason'>[];
  businessUnitId?: number;
}

interface CrmUploadMarker {
  type: 'crmUpload';
  id: string;
  timestamp: Date;
  leadId: number;
  crmUploadReason: 'interested' | null;
}

const Conversation: FC<Props> = ({
  conversation,
  scheduledMessages = [],
  isFilteredConversation = false,
  fullHeight = false,
  compact = false,
  isClientView = false,
  outgoingBubbleColor,
  outgoingBubbleTextColor,
  isAssistantWriting = false,
  hideMessageMeta = false,
  newMessageChunk = '',
  onQuickReplyClick,
  leads,
  businessUnitId,
}) => {
  const isWhatsAppError = (error: string): boolean => {
    return error.includes('131049') || error.includes('130472') || error.includes('131026');
  };

  const isOutgoingMessage = (message: Message): boolean =>
    (!isClientView && message.role !== 'user') || (message.role === 'user' && isClientView);

  const [cancelScheduledMessage] = useCancelScheduledMessageMutation();
  const technicalStatusesForFilter = ['functionSuggestion', 'functionResponse', 'suggestion'];

  const filteredConversation: MessageForDisplay[] = conversation
    .filter((message) => {
      if (!isFilteredConversation) return true;
      if (message.role === 'function') return false;
      if (technicalStatusesForFilter.includes(message.status)) return false;
      if (
        message.role === 'assistant' &&
        message.functionName &&
        (!message.text || !String(message.text).trim()) &&
        !message.fileUrl
      ) {
        return false;
      }
      return (
        !filterOutStatuses.includes(message.status) ||
        (message.status === 'failed' && message.error && isWhatsAppError(message.error))
      );
    })
    .map((message) => ({
      ...message,
      referenceMessage: message.referenceMessageId
        ? conversation.find((m) => m.id === message.referenceMessageId)
        : null,
      carouselCards: message.text?.startsWith('[Carousel enviado]')
        ? conversation.filter((m) => m.referenceMessageId === message.id)
        : undefined,
    }))
    .filter((message) => message.role === 'user' || !message.referenceMessage?.text?.startsWith('[Carousel enviado]'));

  const crmUploadMarkers: CrmUploadMarker[] = useMemo(() => {
    if (!businessUnitId || !leads?.length) return [];
    return leads
      .filter((lead) => lead.CRMUpdateAt)
      .map((lead) => ({
        type: 'crmUpload' as const,
        id: `crm-${lead.id}`,
        timestamp: new Date(lead.CRMUpdateAt as Date),
        leadId: lead.id,
        crmUploadReason: lead.crmUploadReason ?? null,
      }))
      .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  }, [leads, businessUnitId]);

  type TimelineItem =
    | { kind: 'message'; message: MessageForDisplay; sortKey: number }
    | { kind: 'crm'; marker: CrmUploadMarker; sortKey: number }
    | { kind: 'scheduled'; scheduledMessage: ScheduledMessage; sortKey: number };

  const timeline: TimelineItem[] = [
    ...filteredConversation.map<TimelineItem>((message) => ({
      kind: 'message',
      message,
      sortKey: new Date(message.createdAt).getTime(),
    })),
    ...crmUploadMarkers.map<TimelineItem>((marker) => ({
      kind: 'crm',
      marker,
      sortKey: marker.timestamp.getTime(),
    })),
    ...scheduledMessages
      .filter((scheduledMessage) => scheduledMessage.status !== 'sent')
      .map<TimelineItem>((scheduledMessage) => ({
        kind: 'scheduled',
        scheduledMessage,
        sortKey: new Date(scheduledMessage.postAt).getTime(),
      })),
  ].sort((a, b) => a.sortKey - b.sortKey);

  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<number | null>(null);
  const currentUser = useCurrentUser();

  // BCI Refi handles sensitive client documents that only global admins may view.
  // Everyone else (business unit admins and below) sees a placeholder instead of the
  // preview/download for attachments the client sent us.
  const restrictClientMedia =
    businessUnitId === BCI_REFI_ROBOT_BUSINESS_UNIT_ID && !isClientView && !currentUser?.isAdmin;
  const hideAttachment = (message?: Message | null): boolean =>
    restrictClientMedia && !!message && !isOutgoingMessage(message);

  const renderRestrictedAttachment = (fontSize?: number | string) => (
    <Box
      sx={{
        p: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        backgroundColor: SURFACE_CONTAINER_LOW,
        borderRadius: RADIUS_ITEM,
        color: alpha(NAVY, 0.6),
      }}
    >
      <InsertDriveFileIcon sx={{ height: '1.25em', width: '1.25em' }} />
      <Typography variant="body2" sx={{ fontSize }}>
        Archivo no disponible
      </Typography>
    </Box>
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [conversation.length, newMessageChunk, isAssistantWriting]);

  useEffect(() => {
    if (highlightedMessageId !== null) {
      const timer = setTimeout(() => {
        setHighlightedMessageId(null);
      }, 2000);
      return () => {
        clearTimeout(timer);
      };
    }
    return undefined;
  }, [highlightedMessageId]);

  const getMessageError = (error: string): React.ReactNode | null => {
    // 131049 - This message was not delivered to maintain healthy ecosystem engagement. - In order to maintain a healthy ecosystem engagement, the message failed to be delivered.
    // 130472 - User's number is part of an experiment - Failed to send message because this user's phone number is part of an experiment
    if (error.includes('131049') || error.includes('130472')) {
      const errorCode = error.includes('131049') ? '131049' : '130472';
      return (
        <>
          El mensaje no pudo ser enviado porque no se pudo entregar a la red de WhatsApp.{' '}
          <a
            href="https://developers.facebook.com/docs/whatsapp/cloud-api/support/error-codes?locale=es_ES#otros-errores"
            target="_blank"
            rel="noreferrer"
          >
            Busca el código de error ({errorCode}) aquí para más información.
          </a>
        </>
      );
    }
    // 131026 - Message undeliverable - Message Undeliverable.
    if (error.includes('131026')) {
      return 'El mensaje no pudo ser enviado porque el número de teléfono no tiene WhatsApp.';
    }
    return null;
  };

  const getMessageButtons = (buttons: Message['buttons'], onDarkBg = false) =>
    buttons && (
      <Stack
        spacing={1}
        sx={{
          pb: 1,
        }}
      >
        {buttons.map((button) => (
          <div key={`${button.id} - ${button.title}`}>
            <Divider sx={onDarkBg ? { borderColor: alpha('#fff', 0.2) } : undefined} />
            <Box
              sx={{
                textAlign: 'center',
                alignSelf: 'center',
              }}
            >
              {button.url ? (
                <Button
                  variant="text"
                  startIcon={<Launch fontSize="small" />}
                  href={button.url}
                  target="_blank"
                  rel="noreferrer"
                  sx={
                    onDarkBg
                      ? {
                          color: '#fff',
                          '&:hover': { backgroundColor: alpha('#fff', 0.08) },
                        }
                      : undefined
                  }
                >
                  {button.title}
                </Button>
              ) : (
                <Button
                  variant="text"
                  onClick={() => onQuickReplyClick?.(button.id || button.title)}
                  disabled={!onQuickReplyClick}
                  sx={
                    onDarkBg
                      ? {
                          color: '#fff',
                          '&:hover': { backgroundColor: alpha('#fff', 0.08) },
                          '&.Mui-disabled': { color: '#fff' },
                        }
                      : undefined
                  }
                >
                  {button.title}
                </Button>
              )}
            </Box>
          </div>
        ))}
      </Stack>
    );

  const getMessageHTML = (message: MessageForDisplay) => {
    const bodyFontSize = compact ? '0.8125rem' : undefined;
    const captionFontSize = compact ? '0.7rem' : undefined;
    const isOutgoing = isOutgoingMessage(message);
    const isMutedStatus = ['discarded', 'neverSent'].includes(message.status);
    const isOnDarkBg = isOutgoing && !isMutedStatus;
    const replyBg = isOnDarkBg ? alpha('#fff', 0.12) : SURFACE_TINT;
    const replyBorder = isOnDarkBg ? '#fff' : NAVY;
    const replyText = isOnDarkBg ? alpha('#fff', 0.75) : alpha(NAVY, 0.6);
    const filePillBg = isOnDarkBg ? alpha('#fff', 0.12) : SURFACE_TINT;
    const fileLabelColor = isOnDarkBg ? '#fff' : NAVY;
    const captionColor = message.status === 'event' ? alpha(NAVY, 0.6) : isOnDarkBg ? alpha('#fff', 0.7) : 'grey';

    let text = '';
    if (isFilteredConversation) {
      text += message.text ?? '';
    } else if (message.role === 'assistant' && message.functionName && message.functionArguments) {
      text += `Asistente: ${message.functionName}(${message.functionArguments?.replaceAll('\n', '')})`;
    } else if (message.role === 'function') {
      text += `Función: ${message.text?.replaceAll('\\', '').replace(/(^")|("$)/g, '')}`;
    } else {
      text += message.text ?? '';
    }

    if (text.startsWith('[Carousel enviado] ')) {
      text = text.replace('[Carousel enviado] ', '');
    }
    text += '\n';

    const isSpecialStatus = [
      'failed',
      'deleted',
      'discarded',
      'warning',
      'functionSuggestion',
      'functionResponse',
      'suggestion',
      'neverSent',
    ].includes(message.status);

    return (
      <>
        {message.referenceMessage && (
          <Box
            role="button"
            tabIndex={0}
            onClick={() => {
              if (message.referenceMessage?.id) {
                const refElement = messageRefs.current.get(message.referenceMessage.id);
                if (refElement) {
                  setHighlightedMessageId(message.referenceMessage.id);
                  refElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }
            }}
            sx={{
              mb: 1,
              backgroundColor: replyBg,
              borderLeft: `4px solid ${replyBorder}`,
              borderRadius: RADIUS_ITEM,
              color: replyText,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              p: compact ? 0.75 : 1,
              fontSize: bodyFontSize,
            }}
          >
            {message.referenceMessage.fileUrl &&
              (!hideAttachment(message.referenceMessage) &&
              ['png', 'jpeg', 'jpg', 'webp'].includes(getExtension(message.referenceMessage.fileUrl)) ? (
                <Box sx={{ display: 'inline-block' }}>
                  <img
                    src={message.referenceMessage.fileUrl}
                    alt="chat-file"
                    style={{
                      maxHeight: compact ? '48px' : '70px',
                      objectFit: 'contain',
                      marginRight: compact ? '6px' : '10px',
                    }}
                  />
                </Box>
              ) : (
                <InsertDriveFileIcon
                  sx={{
                    height: '1.5em',
                    width: '1.5em',
                  }}
                />
              ))}
            {message.referenceMessage.text && message.referenceMessage.text.length > 100
              ? `${message.referenceMessage.text?.substring(0, 100)}...`
              : message.referenceMessage.text}
            {!message.referenceMessage.text && message.referenceMessage.fileName && (
              <Typography>{message.referenceMessage.fileName.split('-').at(-1)}</Typography>
            )}
          </Box>
        )}
        {['system', 'none'].includes(message.sender as string) &&
          currentUser?.isAdmin &&
          !isFilteredConversation &&
          message.status !== 'event' && (
            <div
              style={{
                position: 'relative',
                float: 'right',
                top: '0px',
              }}
            >
              <IconButton
                size="small"
                title="Abrir en el playground"
                onClick={() => {
                  window.open(`/playground/${message.id}`, '_blank', 'noreferrer');
                }}
                sx={{ color: isOnDarkBg ? '#fff' : NAVY }}
              >
                <ScienceOutlinedIcon fontSize="inherit" />
              </IconButton>
            </div>
          )}
        {message.fileUrl && ['ogg'].includes(getExtension(message.fileUrl)) && (
          // biome-ignore lint/a11y/useMediaCaption: audio messages from WhatsApp don't have captions
          <audio controls src={message.fileUrl} />
        )}
        {text && (!message.fileUrl || getExtension(message.fileUrl) === 'ogg') && (
          <Box
            sx={{
              px: 1,
              whiteSpace: 'pre-line',
              textAlign: 'left',
              wordBreak: 'break-word',
              color: isSpecialStatus ? (isMutedStatus ? NAVY : isOutgoing ? alpha('#fff', 0.65) : 'grey') : undefined,
              fontSize: bodyFontSize,
            }}
          >
            {['discarded', 'neverSent'].includes(message.status) ? text : textToReactNodes(text)}
          </Box>
        )}
        {message.url && !hideAttachment(message) && (
          <a href={message.url} target="_blank" rel="noreferrer">
            <Typography
              variant="body2"
              sx={{
                px: 1,
              }}
            >
              {message.fileUrl}
            </Typography>
          </a>
        )}
        {message.fileUrl &&
          ['png', 'jpeg', 'jpg', 'webp', 'mp4'].includes(getExtension(message.fileUrl)) &&
          hideAttachment(message) &&
          renderRestrictedAttachment(bodyFontSize)}
        {message.fileUrl &&
          ['png', 'jpeg', 'jpg', 'webp', 'mp4'].includes(getExtension(message.fileUrl)) &&
          !hideAttachment(message) && (
            <>
              {getExtension(message.fileUrl) === 'mp4' && (
                <video width="100%" autoPlay muted controls>
                  <source src={message.fileUrl} type="video/mp4" />
                </video>
              )}
              {getExtension(message.fileUrl) !== 'mp4' && (
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setSelectedImageUrl(message.fileUrl);
                  }}
                  sx={{ cursor: 'pointer', display: 'inline-block' }}
                >
                  <img
                    src={message.fileUrl}
                    alt="chat-file"
                    style={{
                      maxWidth: '100%',
                      maxHeight: message.fileUrl.includes('webp')
                        ? compact
                          ? '88px'
                          : '120px'
                        : compact
                          ? '160px'
                          : '250px',
                    }}
                  />
                </Box>
              )}
              <br />
              {text && (
                <Box
                  sx={{
                    whiteSpace: 'pre-line',
                    textAlign: 'left',
                    wordBreak: 'break-word',
                    color: isSpecialStatus
                      ? isMutedStatus
                        ? NAVY
                        : isOutgoing
                          ? alpha('#fff', 0.65)
                          : 'grey'
                      : undefined,
                    fontSize: bodyFontSize,
                  }}
                >
                  {textToReactNodes(
                    text
                      .replace(/Imagen enviada: .+\.(png|jpeg|jpg|webp)/, '')
                      .replace('Imagen enviada ', '')
                      .trim()
                  )}
                </Box>
              )}
            </>
          )}
        {message.carouselCards && (
          <Box
            sx={{
              display: 'flex',
              overflowX: 'auto',
              gap: compact ? 0.5 : 1,
              px: 1,
              pt: 1,
            }}
          >
            {message.carouselCards.map((card) => (
              <Box
                key={card.id}
                sx={{
                  minWidth: compact ? '200px' : '250px',
                  maxWidth: compact ? '200px' : '250px',
                  borderRadius: RADIUS_ITEM,
                  overflow: 'hidden',
                  backgroundColor: SURFACE_CONTAINER_LOWEST,
                  boxShadow: SHADOW,
                }}
              >
                {card.fileUrl && (
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setSelectedImageUrl(card.fileUrl);
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    <img
                      src={card.fileUrl}
                      alt="carousel-card"
                      style={{
                        width: '100%',
                        height: compact ? '140px' : '200px',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                )}
                <Box sx={{ px: 1, color: '#000' }}>
                  {card.text && (
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-line',
                        wordBreak: 'break-word',
                        mb: 1,
                        textAlign: 'left',
                        fontSize: bodyFontSize,
                        color: '#000',
                      }}
                    >
                      {textToReactNodes(card.text)}
                    </Typography>
                  )}
                  {card.buttons && card.buttons.length > 0 && getMessageButtons(card.buttons)}
                </Box>
              </Box>
            ))}
          </Box>
        )}
        {message.fileUrl && !['png', 'jpeg', 'jpg', 'webp', 'mp4', 'ogg'].includes(getExtension(message.fileUrl)) && (
          <Box>
            {hideAttachment(message) ? (
              renderRestrictedAttachment(compact ? '0.8rem' : undefined)
            ) : (
              <Box
                sx={{
                  p: 1,
                  backgroundColor: filePillBg,
                  borderRadius: RADIUS_ITEM,
                }}
              >
                <Button
                  href={message.fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  variant="text"
                  color="inherit"
                  sx={{ textTransform: 'none', width: '100%', display: 'block' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <InsertDriveFileIcon
                        sx={{
                          height: '1.5em',
                          width: '1.5em',
                        }}
                      />
                      <Stack sx={{ pr: compact ? 6 : 10, pl: compact ? 1 : 2, textAlign: 'left' }}>
                        <Typography
                          variant="body1"
                          sx={{ color: fileLabelColor, fontSize: compact ? '0.8rem' : undefined }}
                        >
                          {extractFilenameFromUrl(message.fileUrl)}
                        </Typography>
                        <Typography variant="body1" sx={{ color: fileLabelColor, fontSize: compact ? '0.72rem' : 15 }}>
                          {getExtension(message.fileUrl).toLocaleUpperCase()}
                        </Typography>
                      </Stack>
                    </Box>
                    <DownloadIcon
                      sx={{
                        border: 'solid',
                        borderRadius: '100%',
                        borderWidth: 'thin',
                        opacity: '80%',
                        padding: '2px',
                        height: '1.6em',
                        width: '1.6em',
                      }}
                    />
                  </Box>
                </Button>
              </Box>
            )}
            {message.text && !message.text.includes('Documento enviado') && (
              <Box
                sx={{
                  px: 1,
                  mt: 1,
                  whiteSpace: 'pre-line',
                  textAlign: 'left',
                  wordBreak: 'break-word',
                }}
              >
                {textToReactNodes(message.text)}
              </Box>
            )}
          </Box>
        )}
        <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap' }}>
          {!hideMessageMeta && (
            <Typography variant="body2" sx={{ p: compact ? 0.5 : 1, color: captionColor, fontSize: captionFontSize }}>
              {message.user?.name && `Enviado por ${message.user?.name} - `}
              {new Date(message.createdAt).toLocaleTimeString('es-CL', {
                day: 'numeric',
                month: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: isFilteredConversation ? undefined : '2-digit',
                hour12: false,
              })}
            </Typography>
          )}
          {!hideMessageMeta && message.status === 'sent' && <CheckIcon />}
          {!hideMessageMeta && (message.status === 'delivered' || message.status === 'received') && (
            <>
              <CheckIcon sx={{ marginRight: '-16px' }} />
              <CheckIcon />
            </>
          )}
          {!hideMessageMeta && message.status === 'read' && (
            <>
              <CheckIcon color="info" sx={{ marginRight: '-16px' }} />
              <CheckIcon color="info" />
            </>
          )}
          {!hideMessageMeta && message.status === 'sent to api' && currentUser && <ClockIcon />}
          {isSpecialStatus && !isFilteredConversation && <Typography variant="body2">{message.status}</Typography>}
          {currentUser?.isAdmin && !isFilteredConversation && message.cost > 0 && (
            <Typography
              variant="body2"
              sx={{
                p: 1,
                color: captionColor,
                fontSize: '0.7em',
              }}
            >
              ${message.cost.toFixed(2)} {message.AIModel}
            </Typography>
          )}
          {message.emoji && (
            <div
              style={{
                height: '26px',
                width: '30px',
                position: 'relative',
                float: 'right',
                bottom: '-20px',
                backgroundColor: SURFACE_CONTAINER_LOWEST,
                borderRadius: '50px',
                textAlign: 'center',
                boxShadow: SHADOW,
              }}
            >
              {message.emoji}
            </div>
          )}
        </Box>
        {getMessageButtons(message.buttons, isOnDarkBg)}
        {message.error &&
          (isFilteredConversation
            ? isWhatsAppError(message.error) || getMessageError(message.error) != null
            : currentUser?.isAdmin || isWhatsAppError(message.error)) && (
            <div
              style={{
                backgroundColor: SURFACE_CONTAINER_LOW,
                color: NAVY,
                borderRadius: RADIUS_ITEM,
                textAlign: 'center',
                padding: '4px',
                ...(compact ? { fontSize: '0.75rem' } : {}),
              }}
            >
              {isFilteredConversation
                ? getMessageError(message.error)
                : currentUser?.isAdmin
                  ? message.error
                  : getMessageError(message.error)}
            </div>
          )}
      </>
    );
  };

  const getMessageStyle = (
    message: Message
  ): {
    backgroundColor?: string;
    color?: string;
    textAlign?: string;
    alignSelf?: string;
    pt?: number;
    boxShadow?: string;
  } => {
    if (message.status === 'event') {
      return {
        backgroundColor: '#C2EBF4',
        color: NAVY,
        textAlign: 'center',
        alignSelf: 'center',
        boxShadow: SHADOW_CARD,
      };
    }
    if (isOutgoingMessage(message)) {
      const isMuted = ['discarded', 'neverSent'].includes(message.status);
      return {
        backgroundColor: isMuted ? LIGHT_GREY : (outgoingBubbleColor ?? '#224267'),
        color: isMuted ? NAVY : (outgoingBubbleTextColor ?? '#fff'),
        textAlign: 'end',
        alignSelf: 'flex-end',
        boxShadow: isMuted ? SHADOW_CARD : SHADOW_DARK_CARD,
      };
    }
    return {
      backgroundColor: SURFACE_CONTAINER_LOWEST,
      color: NAVY,
      textAlign: 'start',
      alignSelf: 'flex-start',
      boxShadow: SHADOW_CARD,
    };
  };

  const translateScheduledMessageStatus = (status: ScheduledMessage['status']) => {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'sent':
        return 'Enviado';
      case 'failed':
        return 'Fallido';
      case 'cancelled':
        return 'Cancelado';
      default:
        return status;
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        height: fullHeight ? '100%' : undefined,
        display: 'flex',
        flexDirection: 'column',
        px: compact ? 0.5 : 1,
        overflow: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {timeline.map((item) => {
          if (item.kind === 'crm') {
            const uploadedAt = item.marker.timestamp.toLocaleString('es-CL', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            });
            const interestLabel = item.marker.crmUploadReason === 'interested' ? 'Interesado' : 'No interesado';
            return (
              <Box
                key={item.marker.id}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: compact ? 1 : 1.5,
                  mb: compact ? 0.5 : 1,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#C2EBF4',
                    color: NAVY,
                    px: compact ? 3 : 5,
                    py: compact ? 0.75 : 1,
                    borderRadius: 999,
                    boxShadow: SHADOW_CARD,
                    textAlign: 'center',
                    lineHeight: 1.2,
                    minWidth: compact ? 220 : 280,
                  }}
                >
                  <Box
                    sx={{
                      fontWeight: 600,
                      fontSize: compact ? '0.75rem' : '0.8125rem',
                      letterSpacing: 0.2,
                    }}
                  >
                    Cliente enviado a CRM
                  </Box>
                  <Box
                    sx={{
                      fontWeight: 400,
                      fontSize: compact ? '0.65rem' : '0.7rem',
                      opacity: 0.75,
                      mt: 0.25,
                    }}
                  >
                    {uploadedAt} — {interestLabel}
                  </Box>
                </Box>
              </Box>
            );
          }

          if (item.kind === 'scheduled') {
            const { scheduledMessage } = item;
            return (
              <Box
                key={`scheduled-${scheduledMessage.id}`}
                sx={{
                  mt: 1,
                  pb: 0,
                  maxWidth: '70%',
                  textAlign: 'end',
                  alignSelf: 'flex-end',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                {scheduledMessage.status !== 'cancelled' && !scheduledMessage.whatsAppCampaignId && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (window.confirm('¿Estás seguro de que quieres cancelar este mensaje programado?')) {
                        cancelScheduledMessage({
                          clientId: scheduledMessage.clientId,
                          scheduledMessageId: scheduledMessage.id,
                        }).catch(console.error);
                      }
                    }}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#e0e0e0',
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" color="error" />
                  </IconButton>
                )}
                {scheduledMessage.whatsAppCampaignId && (
                  <Tooltip title="Mensaje fue programado para enviarse por una campaña.">
                    <CampaignIcon fontSize="medium" sx={{ mr: 1, color: '#224267' }} />
                  </Tooltip>
                )}
                <Box
                  sx={{
                    p: 1,
                    borderRadius: RADIUS_CARD,
                    backgroundColor: '#224267',
                    color: '#fff',
                    textAlign: 'left',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: SHADOW_CARD,
                  }}
                >
                  <Box sx={{ px: 1, whiteSpace: 'pre-line', textAlign: 'left', wordBreak: 'break-word' }}>
                    {textToReactNodes(scheduledMessage.text)}
                  </Box>
                  <Box sx={{ display: 'inline-flex', pt: 1, justifyContent: 'flex-end' }}>
                    <Typography variant="body2" sx={{ color: alpha('#fff', 0.7), mr: 1 }}>
                      Fecha envío{' '}
                      {new Date(scheduledMessage.postAt).toLocaleTimeString('es-CL', {
                        day: 'numeric',
                        month: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })}
                    </Typography>
                    <ClockIcon fontSize="small" />
                    {scheduledMessage.status !== 'pending' && (
                      <Typography variant="body2">
                        {translateScheduledMessageStatus(scheduledMessage.status)}
                      </Typography>
                    )}
                  </Box>
                  {scheduledMessage.buttons && getMessageButtons(scheduledMessage.buttons, true)}
                </Box>
              </Box>
            );
          }

          const { message } = item;
          const messageStyle = getMessageStyle(message);
          let justifyContent = 'flex-start';
          if (messageStyle.alignSelf === 'flex-end') {
            justifyContent = 'flex-end';
          } else if (messageStyle.alignSelf === 'center') {
            justifyContent = 'center';
          }

          const isCarousel = message.carouselCards && message.carouselCards.length > 0;

          return (
            <Box
              key={message.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: justifyContent,
              }}
            >
              {message.isFollowUp && (
                <Tooltip title="Mensaje de follow up, estos son programados para enviarse.">
                  <AlarmIcon fontSize={compact ? 'small' : 'medium'} sx={{ mx: compact ? 0.5 : 1, color: '#224267' }} />
                </Tooltip>
              )}
              {message.userId && (
                <Tooltip title="Mensaje fue enviado por un usuario.">
                  <PersonIcon
                    fontSize={compact ? 'small' : 'medium'}
                    sx={{ mx: compact ? 0.5 : 1, color: '#224267' }}
                  />
                </Tooltip>
              )}
              {message.scheduledMessageId && (
                <Tooltip title="Mensaje fue enviado por una campaña programada.">
                  <CampaignIcon
                    fontSize={compact ? 'small' : 'medium'}
                    sx={{ mx: compact ? 0.5 : 1, color: '#224267' }}
                  />
                </Tooltip>
              )}
              <Box
                ref={(el: HTMLDivElement | null) => {
                  if (el) {
                    messageRefs.current.set(message.id, el);
                  } else {
                    messageRefs.current.delete(message.id);
                  }
                }}
                sx={{
                  mt: compact ? 0.5 : 1,
                  p: compact ? 0.75 : 1,
                  pb: message.error ? 1 : 0,
                  boxShadow: SHADOW_CARD,
                  maxWidth: isCarousel ? (compact ? '94%' : '90%') : compact ? '88%' : '70%',
                  borderRadius: compact ? RADIUS_ITEM : RADIUS_CARD,
                  ...messageStyle,
                  '& a': {
                    color: isOutgoingMessage(message)
                      ? ['discarded', 'neverSent'].includes(message.status)
                        ? '#224267'
                        : '#9FC6EA'
                      : undefined,
                    textDecoration: 'underline',
                  },
                  ...(highlightedMessageId === message.id && {
                    outline: `3px solid ${YELLOW}`,
                    outlineOffset: '2px',
                    transition: 'outline 0.2s ease-in-out',
                  }),
                }}
              >
                {getMessageHTML(message)}
              </Box>
              {message.fileUrl?.includes('referral') && (
                <Tooltip title="Mensaje fue recibido por una campaña de RRSS.">
                  <CampaignIcon
                    fontSize={compact ? 'small' : 'medium'}
                    sx={{ mx: compact ? 0.5 : 1, color: '#224267' }}
                  />
                </Tooltip>
              )}
            </Box>
          );
        })}
        {isAssistantWriting && (
          <Box
            sx={{
              mt: 1,
              p: 1,
              pb: 0,
              maxWidth: '70%',
              borderRadius: RADIUS_CARD,
              backgroundColor: SURFACE_CONTAINER_LOWEST,
              color: NAVY,
              textAlign: 'center',
              width: newMessageChunk ? undefined : 'min-content',
            }}
          >
            {newMessageChunk && (
              <Box
                sx={{
                  px: 1,
                  whiteSpace: 'pre-line',
                  textAlign: 'left',
                  wordBreak: 'break-word',
                }}
              >
                {textToReactNodes(newMessageChunk)}
              </Box>
            )}
            <Box
              sx={{
                px: 2,
                pt: 1,
                display: 'flex',

                '@keyframes bounce': {
                  '0%, 80%, 100%': { transform: 'translateY(0)' },
                  '40%': { transform: 'translateY(-8px)' },
                },

                span: {
                  animation: 'bounce 1.4s infinite',
                  fontSize: '32px',
                  lineHeight: '28px',
                  fontWeight: 600,
                },
              }}
            >
              <span key="dot-1">•</span>
              <span key="dot-2" style={{ animationDelay: '0.15s' }}>
                •
              </span>
              <span key="dot-3" style={{ animationDelay: '0.3s' }}>
                •
              </span>
            </Box>
          </Box>
        )}
        <Dialog
          open={!!selectedImageUrl}
          onClose={() => setSelectedImageUrl(null)}
          maxWidth={false}
          slotProps={{
            paper: {
              sx: {
                backgroundColor: 'transparent',
                boxShadow: 'none',
                maxWidth: '90vw',
                maxHeight: '90vh',
              },
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            {selectedImageUrl && (
              <img
                src={selectedImageUrl}
                alt="chat-file-fullscreen"
                style={{
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                }}
              />
            )}
          </Box>
        </Dialog>
        <div ref={messagesEndRef} style={{ minHeight: '10px', opacity: 0 }} />
      </Box>
    </Box>
  );
};

export default Conversation;
